import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { salesService } from '../../services';
import debounce from 'lodash/debounce';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CONTROL_STYLE } from '../../constants/invoiceConstants';

const formatCurrency = (amount, currencyIcon) => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  if (currencyIcon) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <img src={currencyIcon} alt="currency" style={{ width: '15px', height: '15px', objectFit: 'contain' }} />
        <span>{formatted}</span>
      </Box>
    );
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'SAR',
    minimumFractionDigits: 2
  }).format(amount);
};


const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};


const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'partial':
      return 'warning';
    case 'pending':
      return 'error';
    default:
      return 'default';
  }
};


export default function SelectInvoiceDialog({ open, onClose, onSelectInvoice, dialogTitle = 'Select Invoice for Credit Note', currencyIcon }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalRows, setTotalRows] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Debounce search query update
  const debouncedSetSearchQuery = useMemo(
    () => debounce((value) => {
      setSearchQuery(value);
      setPage(0); // Reset page on new search
    }, 300),
    []
  );

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    debouncedSetSearchQuery(e.target.value);
  }, [debouncedSetSearchQuery]);

  const fetchInvoices = useCallback(async () => {
    if (!open) return;

    setLoading(true);
    try {
      let data;
      if (searchQuery.trim()) {
        data = await salesService.searchSalesInvoices(searchQuery.trim(), page + 1, rowsPerPage);
      } else {
        data = await salesService.getSalesInvoices({}, page + 1, rowsPerPage);
      }

      if (data && data.success) {
        setInvoices(data.data || []);
        setTotalRows(data.pagination?.total || 0);
      } else {
        setInvoices([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [open, page, rowsPerPage, searchQuery]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSearchInput('');
      setInvoices([]);
      setPage(0);
    }
  }, [open]);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSelectInvoice = async (invoice) => {
    try {
      setLoading(true);
      const data = await salesService.getSalesInvoiceByInvoiceNo(invoice.invoiceNo, true);
      if (data.success) {
        onSelectInvoice(data);
        onClose();
      } else {
        alert('Failed to fetch invoice details. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      alert('Error fetching invoice details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        p: 2
      }}>
        <Typography variant="h6" fontWeight={600}>
          {dialogTitle}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Search and Filters */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5
          }}>
            <TextField
              placeholder="Search by invoice number or customer name"
              value={searchInput}
              onChange={handleSearchChange}
              fullWidth
              size="small"
              sx={{
                ...CONTROL_STYLE,
                mt: 1.5,
                '& .MuiInputBase-root': {
                  height: '32px'
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />
          </Box>
        </Box>

        {/* Invoices Table */}
        <TableContainer component={Paper} sx={{
          boxShadow: 'none',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: '0px',
          mb: 2
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{
                backgroundColor: '#f5f5f5',
                '& th': {
                  py: 1,
                  fontSize: '0.75rem',
                  fontWeight: 700
                }
              }}>
                <TableCell>{t('Common.invoice')} #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No invoices found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow
                    key={invoice._id || invoice.invoiceNo}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer'
                      },
                      '& td': {
                        py: 0.75
                      }
                    }}
                    onClick={() => handleSelectInvoice(invoice)}
                  >
                    <TableCell>{invoice.invoiceNo}</TableCell>
                    <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {invoice.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invoice.customerPhone || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatCurrency(invoice.totalAmt, currencyIcon)}</TableCell>
                    <TableCell>
                      <Chip
                        label={invoice.paymentStatus || 'N/A'}
                        size="small"
                        color={getStatusColor(invoice.paymentStatus?.toLowerCase())}
                        sx={{
                          height: 24,
                          textTransform: 'capitalize',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectInvoice(invoice);
                        }}
                        sx={{
                          borderRadius: 0,
                          py: 0.25,
                          px: 1,
                          minWidth: 'auto',
                          textTransform: 'none',
                          fontSize: '0.75rem'
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            },
            '.MuiTablePagination-select': {
              ...CONTROL_STYLE
            },
            '.MuiToolbar-root': {
              minHeight: '40px',
              height: '40px',
              paddingTop: 0,
              paddingBottom: 0
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}